'use client'

import type { TabbedContentBlock as TabbedContentProps } from '@/payload-types'
import React, { useState, useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import RichText from '@/components/RichText'
import { CMSLink } from '@/components/Link'
import { Media } from '@/components/Media'
import BigText from '@/components/sections/BigText'
import { TermineWithCategoryFetch } from '@/components/Termine/TermineWithCategoryFetch'

// Import Swiper styles
import 'swiper/css'

export const TabbedContentBlock: React.FC<
  TabbedContentProps & {
    id?: string
  }
> = (props) => {
  const { id, tabs, bigText } = props
  const [activeTab, setActiveTab] = useState(0)
  const swiperRef = useRef<SwiperType | null>(null)

  if (!tabs || tabs.length === 0) {
    return null
  }

  const handleTabClick = (index: number) => {
    setActiveTab(index)
    swiperRef.current?.slideTo(index)
  }

  const handleSlideChange = (swiper: SwiperType) => {
    setActiveTab(swiper.activeIndex)
  }

  return (
    <div className="relative py-8 md:py-16" id={`block-${id}`}>
      {/* Tab Navigation - scrollable on mobile, fixed on desktop */}
      <div className="container mx-auto px-1 md:px-4">
        <div className="scrollbar-hide overflow-x-auto scroll-smooth">
          <div className="flex min-w-max gap-0">
            {tabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => handleTabClick(index)}
                className={`py-3 px-4 text-lg xs:text-xl sm:text-2xl md:text-3xl rounded-t-xl transition-all duration-300 border border-blue-50 whitespace-nowrap flex-shrink-0 md:flex-1 ${
                  activeTab === index
                    ? 'bg-custom-blue text-gray-900 scale-105'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-custom-blue hover:scale-102'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-24 bg-custom-blue py-8 md:py-16" id={`block-${id}`}>
        <div className="container px-4">
          <div className="relative overflow-hidden">
            <Swiper
              spaceBetween={0}
              slidesPerView={1}
              autoHeight={true}
              onSwiper={(swiper) => {
                swiperRef.current = swiper
              }}
              onSlideChange={handleSlideChange}
              className="tabbed-content-swiper"
              touchEventsTarget="container"
              touchStartPreventDefault={false}
              grabCursor={true}
              allowTouchMove={true}
              speed={500}
              breakpoints={{
                320: {
                  allowTouchMove: true,
                  touchEventsTarget: 'container',
                },
                768: {
                  allowTouchMove: true,
                  touchEventsTarget: 'container',
                },
              }}
            >
              {tabs.map((tab, index) => {
                const tabHasImage = tab?.image

                return (
                  <SwiperSlide key={index}>
                    <div className="flex w-full flex-col items-start gap-8 md:gap-12 lg:flex-row">
                      {/* Left Side - Image (only if image exists) */}
                      {tabHasImage && (
                        <div className="w-full lg:w-1/2">
                          <div className="aspect-4/3 overflow-hidden rounded-lg transition-all duration-500 ease-out">
                            <Media
                              resource={tab.image}
                              className="h-full w-full object-cover transition-all duration-500 ease-out"
                              imgClassName="h-full w-full object-cover transition-all duration-500 ease-out"
                            />
                          </div>
                        </div>
                      )}

                      {/* Right Side - Content */}
                      <div
                        className={` ${!tabHasImage ? 'w-full lg:w-1/2 flex-1' : 'w-full lg:w-1/2'}`}
                      >
                        {tab?.smallText && (
                          <p className="mb-1 text-label-bold uppercase transition-all duration-300 ease-out md:mb-4">
                            {tab.smallText}
                          </p>
                        )}

                        {tab?.headline && (
                          <h2 className="mb-2 text-gray-900 transition-all duration-300 ease-out md:mb-8">
                            {tab.headline}
                          </h2>
                        )}

                        {/* Show Termine component if includeTermine is true, otherwise show regular content */}
                        {tab?.includeTermine && tab?.campKategorie ? (
                          <TermineWithCategoryFetch
                            categoryId={
                              typeof tab.campKategorie === 'string'
                                ? tab.campKategorie
                                : tab.campKategorie.id
                            }
                            showPastEvents={tab.showPastEvents ?? false}
                            maxCamps={tab.maxCamps ?? undefined}
                          />
                        ) : (
                          <>
                            {tab?.text && (
                              <div className="mb-2 overflow-hidden text-gray-700 transition-all duration-300 ease-out md:mb-8">
                                <RichText
                                  data={tab.text}
                                  enableGutter={false}
                                  enableProse={true}
                                  className="max-w-full break-normal text-base"
                                />
                              </div>
                            )}

                            {tab?.link && (
                              <div className="pt-4 underline transition-all duration-300 ease-out hover:translate-x-2">
                                <CMSLink {...tab.link}></CMSLink>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </SwiperSlide>
                )
              })}
            </Swiper>
          </div>
        </div>
      </div>

      {/* Big Text at the bottom */}
      {bigText && (
        <div className="pointer-events-none">
          <BigText text={bigText} light={true} bottomPosition="bottom-[100px]" />
        </div>
      )}
    </div>
  )
}
